import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import mitt from 'mitt'
import { locationService, sessionService, localDb } from '@/services'
import { useAuthStore } from './auth'

export const useLocationStore = defineStore('location', () => {
  // Simple app-wide event bus for location change notifications
  const emitter = mitt()
  const authStore = useAuthStore()
  // State
  const locations = ref([])
  const currentLocation = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const initialized = ref(false)

  // Freshness/in-flight guards
  let fetchLocationsInflight = null
  let locationsLastFetchedAt = 0
  let initCurrentLocationInflight = null

  // Getters
  const getLocations = computed(() => locations.value)
  const getCurrentLocation = computed(() => currentLocation.value)
  const isLoading = computed(() => loading.value)
  const getError = computed(() => error.value)
  const isInitialized = computed(() => initialized.value)

  // Actions
  const hydrateFromCache = async () => {
    try {
      const cached = await localDb.cacheGetLocations()
      if (!cached) return false
      const cachedLocations = cached.locations || []
      if (!Array.isArray(cachedLocations) || cachedLocations.length === 0) return false

      locations.value = cachedLocations
      const desiredId = cached.currentLocationId
      if (desiredId) {
        currentLocation.value = cachedLocations.find(l => l.id === desiredId) || null
      }
      if (!currentLocation.value) {
        currentLocation.value = cachedLocations[0] || null
      }

      initialized.value = true
      return true
    } catch (e) {
      console.warn('Failed to hydrate locations from local DB:', e)
      return false
    }
  }

  const persistCache = async () => {
    try {
      await localDb.cacheSetLocations({
        locations: locations.value || [],
        currentLocationId: currentLocation.value?.id || null
      })
    } catch (e) {
      console.warn('Failed to persist locations cache to local DB:', e)
    }
  }

  const fetchLocations = async ({ force = false } = {}) => {
    if (!authStore.isAuthenticated) {
      return null
    }

    // If we already have fresh data, return it
    const now = Date.now()
    if (!force && locations.value.length > 0 && currentLocation.value && (now - locationsLastFetchedAt < 5 * 60 * 1000)) {
      return locations.value
    }
    if (!force && fetchLocationsInflight) return fetchLocationsInflight

    loading.value = true
    error.value = null
    try {
      fetchLocationsInflight = sessionService.getAccessibleLocations()
      const response = await fetchLocationsInflight
      if (!authStore.isAuthenticated) {
        return null
      }

      locations.value = response.locations || []

      // Set current location from the session response
      if (response.current_location_id) {
        const location = locations.value.find(loc => loc.id === response.current_location_id)
        if (location) {
          currentLocation.value = location
        }
      }

      // Fallback to first location if no current location is set
      if (!currentLocation.value && locations.value.length > 0) {
        currentLocation.value = locations.value[0]
      }

      await persistCache()
      initialized.value = true
      locationsLastFetchedAt = Date.now()
      return locations.value
    } catch (err) {
      if (!authStore.isAuthenticated || err?.response?.status === 401) {
        return null
      }
      // Phase 1 offline fallback: hydrate from local DB if network fails.
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (offline || !err?.response) {
        const ok = await hydrateFromCache()
        if (ok) return locations.value
      }
      error.value = err.message || 'Failed to fetch locations'
      throw err
    } finally {
      loading.value = false
      fetchLocationsInflight = null
    }
  }

  const fetchLocation = async (id, { force = false } = {}) => {
    try {
      loading.value = true
      error.value = null

      // If we already have this location in cache and not forcing, return it
      const cached = locations.value.find(loc => loc.id === id)
      if (cached && !force) {
        return cached
      }

      const location = await locationService.getLocation(id)

      // Upsert into cache
      const idx = locations.value.findIndex(loc => loc.id === id)
      if (idx !== -1) {
        locations.value[idx] = location
      } else {
        locations.value.push(location)
      }
      return location
    } catch (err) {
      error.value = err.message || 'Failed to fetch location'
      throw err
    } finally {
      loading.value = false
    }
  }

  const createLocation = async (locationData) => {
    try {
      loading.value = true
      error.value = null
      const newLocation = await locationService.createLocation(locationData)
      locations.value.push(newLocation)
      await persistCache()
      return newLocation
    } catch (err) {
      error.value = err.message || 'Failed to create location'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateLocation = async (id, locationData) => {
    try {
      loading.value = true
      error.value = null
      const updatedLocation = await locationService.updateLocation(id, locationData)

      // Update in locations array
      const index = locations.value.findIndex(loc => loc.id === id)
      if (index !== -1) {
        locations.value[index] = updatedLocation
      }

      // Update current location if it's the one being updated
      if (currentLocation.value && currentLocation.value.id === id) {
        currentLocation.value = updatedLocation
      }

      await persistCache()
      return updatedLocation
    } catch (err) {
      error.value = err.message || 'Failed to update location'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteLocation = async (id) => {
    try {
      loading.value = true
      error.value = null
      await locationService.deleteLocation(id)

      // Remove from locations array
      locations.value = locations.value.filter(loc => loc.id !== id)

      // Clear current location if it's the one being deleted
      if (currentLocation.value && currentLocation.value.id === id) {
        currentLocation.value = locations.value.length > 0 ? locations.value[0] : null
      }

      await persistCache()
      return true
    } catch (err) {
      error.value = err.message || 'Failed to delete location'
      throw err
    } finally {
      loading.value = false
    }
  }

  const setCurrentLocation = async (location, options = {}) => {
    const targetId = location?.id
    if (!targetId) {
      throw new Error('Invalid location selected')
    }
    if (currentLocation.value?.id === targetId) {
      return currentLocation.value
    }

    const previousLocation = currentLocation.value
    try {
      error.value = null

      if (typeof options.guard === 'function') {
        const guardResult = await options.guard({
          currentLocation: previousLocation,
          targetLocation: location
        })
        const blocked = guardResult === false || (guardResult && guardResult.allowed === false)
        if (blocked) {
          const message = (guardResult && guardResult.reason) || 'Location change is not allowed right now'
          const guardError = new Error(message)
          guardError.blocked = true
          throw guardError
        }
      }

      // Persist to backend session using secure API
      const response = await sessionService.setCurrentLocationInSession(targetId)
      const resolvedId = response?.location_id || targetId
      const resolvedLocation = locations.value.find(loc => loc.id === resolvedId) || location
      currentLocation.value = resolvedLocation

      // Fire a public event so pages can react to location changes
      emitter.emit('location:changed', { location: resolvedLocation, previousLocation })

      await persistCache()
      return resolvedLocation
    } catch (err) {
      // Revert local state if guard/backend call fails
      currentLocation.value = previousLocation
      error.value = err.message || 'Failed to update session location'
      throw err
    }
  }

  const initializeCurrentLocation = async () => {
    if (!authStore.isAuthenticated) {
      return null
    }
    // If already set, skip network call
    if (currentLocation.value) return currentLocation.value
    if (initCurrentLocationInflight) return initCurrentLocationInflight
    try {
      initCurrentLocationInflight = sessionService.getCurrentLocationFromSession()
      const response = await initCurrentLocationInflight
      if (!authStore.isAuthenticated) {
        return null
      }
      const locationId = response.location_id

      // Find the location in our loaded locations
      const location = locations.value.find(loc => loc.id === locationId)

      if (location) {
        currentLocation.value = location
      } else {
        // Fallback to first location if not found
        if (locations.value.length > 0 && !currentLocation.value) {
          currentLocation.value = locations.value[0]
        }
      }
      return currentLocation.value
    } catch (err) {
      if (!authStore.isAuthenticated || err?.response?.status === 401) {
        return null
      }
      console.warn('Failed to get current location from session:', err)
      // Fallback to first location if session call fails
      if (locations.value.length > 0 && !currentLocation.value) {
        currentLocation.value = locations.value[0]
      }
      return currentLocation.value
    } finally {
      initCurrentLocationInflight = null
    }
  }

  const clearError = () => {
    error.value = null
  }

  const hydrateFromBootstrap = async ({
    locations: nextLocations = [],
    current_location_id: currentLocationId = null
  } = {}) => {
    locations.value = Array.isArray(nextLocations) ? nextLocations : []

    if (currentLocationId) {
      const location = locations.value.find(loc => loc.id === currentLocationId)
      if (location) {
        currentLocation.value = location
      }
    }

    if (!currentLocation.value && locations.value.length > 0) {
      currentLocation.value = locations.value[0]
    }

    initialized.value = true
    locationsLastFetchedAt = Date.now()
    error.value = null
    await persistCache()
    return currentLocation.value
  }

  const resetOnLogout = () => {
    locations.value = []
    currentLocation.value = null
    loading.value = false
    error.value = null
    initialized.value = false
    fetchLocationsInflight = null
    locationsLastFetchedAt = 0
    initCurrentLocationInflight = null
  }

  return {
    // State
    locations,
    currentLocation,
    loading,
    error,
    initialized,

    // Getters
    getLocations,
    getCurrentLocation,
    isLoading,
    getError,
    isInitialized,

    // Actions
    fetchLocations,
    fetchLocation,
    createLocation,
    updateLocation,
    deleteLocation,
    setCurrentLocation,
    initializeCurrentLocation,
    clearError,
    resetOnLogout,
    hydrateFromCache,
    hydrateFromBootstrap,

    // Events
    onLocationChanged: (handler) => emitter.on('location:changed', handler),
    offLocationChanged: (handler) => emitter.off('location:changed', handler)
  }
})
